/**
 * Change your own password (POST /api/auth/password). The API checks the current
 * password and the same rules as a reset, then signs out every other session.
 */
import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { ApiError, post } from '../lib/api.ts';
import { Button, Callout, Dialog, Field, useToast } from './ui.tsx';

export function ChangePasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mismatch, setMismatch] = useState(false);
  const close = () => {
    setCurrent('');
    setNext('');
    setConfirm('');
    setMismatch(false);
    m.reset();
    onClose();
  };
  const m = useMutation({
    mutationFn: () => post('/api/auth/password', { current, next }),
    onSuccess: () => {
      toast({ title: 'Password changed', body: 'Other devices were signed out.' });
      close();
    },
  });
  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (next !== confirm) return setMismatch(true);
    setMismatch(false);
    m.mutate();
  };
  return (
    <Dialog
      open={open}
      onClose={close}
      eyebrow="Your account"
      title="Change your password"
      width={460}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button variant="primary" loading={m.isPending} disabled={!current || !next || !confirm} onClick={() => submit()}>
            Change password
          </Button>
        </>
      }
    >
      <form className="stack" onSubmit={submit}>
        <Field label="Current password">{(p) => <input {...p} className="input" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />}</Field>
        <Field label="New password" hint="At least 10 characters. A short sentence works well.">
          {(p) => <input {...p} className="input" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />}
        </Field>
        <Field label="Confirm new password" error={mismatch ? 'The two passwords don’t match.' : null}>
          {(p) => <input {...p} className="input" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />}
        </Field>
        <p className="faint" style={{ fontSize: 'var(--step--2)' }}>You stay signed in here. Hearth signs you out on your other devices.</p>
        {m.error ? <Callout tone="bad">{m.error instanceof ApiError ? m.error.message : 'Something went wrong.'}</Callout> : null}
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}
